import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BarChart3, TrendingUp, Target, Users, MapPin, ArrowRight, Bell, User, Settings, Clock, Download, RefreshCw, ChevronDown, AlertTriangle, DollarSign } from 'lucide-react';
import KPICard from '../components/executive_dashboard/KPICard';
import NotificationPanel from '../components/notifications/NotificationPanel';
import { toast } from 'react-hot-toast';
const Home = () => {
  const navigate = useNavigate();
  const [notificationCount, setNotificationCount] = useState(4);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [showNotifications, setShowNotifications] = useState(false);
  const [platformStatus, setPlatformStatus] = useState('warning');
  const [userSettings, setUserSettings] = useState({
    role: 'Executive Manager',
    preferences: {
      defaultView: 'executive-dashboard',
      refreshInterval: 5,
      dataExportFormat: 'excel'
    }
  });
  const modules = [{
    title: 'Executive Dashboard',
    description: 'High-level performance metrics and KPIs for strategic decision making',
    icon: <BarChart3 size={24} className="text-purple-600" />,
    path: '/executive-dashboard',
    color: 'bg-purple-50',
    borderColor: 'border-purple-200',
    status: 'healthy',
    metrics: [{
      label: 'Volume',
      value: '7,075 MML',
      trend: 'up',
      performance: 'good'
    }, {
      label: 'Growth',
      value: '+9.3% QoQ',
      trend: 'up',
      performance: 'good'
    }, {
      label: 'Stations',
      value: '2,343',
      trend: 'up',
      performance: 'good'
    }]
  }, {
    title: 'Segmentation Explorer',
    description: 'Customer segmentation insights to tailor marketing strategies',
    icon: <Users size={24} className="text-blue-600" />,
    path: '/segmentation-explorer',
    color: 'bg-blue-50',
    borderColor: 'border-blue-200',
    status: 'warning',
    metrics: [{
      label: 'Segments',
      value: '4',
      trend: 'neutral',
      performance: 'neutral'
    }, {
      label: 'Customer Data',
      value: '98.2M',
      trend: 'up',
      performance: 'good'
    }, {
      label: 'Precision',
      value: '91.4%',
      trend: 'down',
      performance: 'warning'
    }]
  }, {
    title: 'Trade Area Analyzer',
    description: 'Local market intelligence for optimizing station performance',
    icon: <MapPin size={24} className="text-green-600" />,
    path: '/trade-area-analyzer',
    color: 'bg-green-50',
    borderColor: 'border-green-200',
    status: 'warning',
    metrics: [{
      label: 'Areas',
      value: '1,254',
      trend: 'up',
      performance: 'good'
    }, {
      label: 'Avg. Radius',
      value: '3.2 km',
      trend: 'neutral',
      performance: 'neutral'
    }, {
      label: 'Competitors',
      value: '4,813',
      trend: 'up',
      performance: 'warning'
    }]
  }, {
    title: 'Competitor 360',
    description: 'Comprehensive competitive intelligence across all markets',
    icon: <TrendingUp size={24} className="text-indigo-600" />,
    path: '/competitor-360',
    color: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    status: 'healthy',
    metrics: [{
      label: 'Monitored',
      value: '42 chains',
      trend: 'up',
      performance: 'good'
    }, {
      label: 'Price Points',
      value: '8.3M',
      trend: 'up',
      performance: 'good'
    }, {
      label: 'Updates',
      value: 'Hourly',
      trend: 'neutral',
      performance: 'neutral'
    }]
  }, {
    title: 'Campaign Management',
    description: 'End-to-end management of marketing promotions and campaigns',
    icon: <Target size={24} className="text-red-600" />,
    path: '/campaign-management',
    color: 'bg-red-50',
    borderColor: 'border-red-200',
    status: 'critical',
    metrics: [{
      label: 'Active',
      value: '2',
      trend: 'neutral',
      performance: 'neutral'
    }, {
      label: 'Scheduled',
      value: '2',
      trend: 'down',
      performance: 'warning'
    }, {
      label: 'ROI',
      value: '+1.1x',
      trend: 'down',
      performance: 'critical'
    }]
  }];
  const frequentlyAccessed = [{
    title: 'Campaign Management',
    path: '/campaign-management',
    icon: <Target size={16} className="text-red-600" />,
    lastAccessed: '2 hours ago'
  }, {
    title: 'Executive Dashboard',
    path: '/executive-dashboard',
    icon: <BarChart3 size={16} className="text-purple-600" />,
    lastAccessed: '1 day ago'
  }, {
    title: 'Trade Area Analyzer',
    path: '/trade-area-analyzer',
    icon: <MapPin size={16} className="text-green-600" />,
    lastAccessed: '3 days ago'
  }];
  const refreshData = () => {
    setLastUpdated(new Date());
  };
  useEffect(() => {
    const interval = setInterval(() => {
      refreshData();
    }, userSettings.preferences.refreshInterval * 60000);
    return () => clearInterval(interval);
  }, [userSettings.preferences.refreshInterval]);
  const getStatusColor = status => {
    switch (status) {
      case 'healthy':
        return 'bg-green-500';
      case 'warning':
        return 'bg-amber-500';
      case 'critical':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };
  const getPerformanceColor = performance => {
    switch (performance) {
      case 'good':
        return 'text-green-600';
      case 'warning':
        return 'text-amber-600';
      case 'critical':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };
  const getTrendIcon = trend => {
    switch (trend) {
      case 'up':
        return <TrendingUp size={12} className="text-green-600" />;
      case 'down':
        return <TrendingUp size={12} className="text-red-600 transform rotate-180" />;
      default:
        return null;
    }
  };
  const exportData = () => {
    alert('Exporting data to ' + userSettings.preferences.dataExportFormat);
  };
  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };
  const handleActionComplete = notification => {
    setNotificationCount(prev => Math.max(0, prev - 1));
    toast.success(`Action completed: ${notification.title}`);
    if (notification.actionPath) {
      navigate(notification.actionPath);
    }
    if (notification.type === 'alert' && notification.category === 'campaign') {
      const updatedModules = [...modules];
      const campaignModule = updatedModules.find(m => m.title === 'Campaign Management');
      if (campaignModule) {
        campaignModule.status = 'warning';
        campaignModule.metrics.find(m => m.label === 'ROI').value = '+1.4x';
        campaignModule.metrics.find(m => m.label === 'ROI').performance = 'warning';
      }
      refreshData();
    }
  };
  return <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-purple-900">Overview</h1>
              <span className="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                v2.0
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center px-2 py-1 rounded-md bg-gray-50 border border-gray-200">
                <span className={`h-2.5 w-2.5 rounded-full ${getStatusColor(platformStatus)} mr-2`}></span>
                <span className="text-xs text-gray-600 font-medium">
                  {platformStatus === 'healthy' ? 'All Systems Online' : platformStatus === 'warning' ? 'Requires Attention' : 'Critical Issues'}
                </span>
              </div>
              
              <button onClick={refreshData} className="p-2 text-gray-500 hover:text-purple-600 flex items-center text-sm group relative">
                <RefreshCw size={18} className="mr-2 group-hover:rotate-180 transition-transform duration-500" />
                <span>Updated: {lastUpdated.toLocaleTimeString()}</span>
                <span className="absolute -bottom-8 left-0 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Auto-refreshes every {userSettings.preferences.refreshInterval} minutes
                </span>
              </button>
              
              <button onClick={exportData} className="p-2 text-gray-600 hover:text-purple-600 rounded-full hover:bg-gray-100 relative group">
                <Download size={20} />
                <span className="absolute -bottom-8 right-0 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Export to {userSettings.preferences.dataExportFormat}
                </span>
              </button>
              
              <div className="relative">
                <button className="p-2 text-gray-600 hover:text-purple-600 rounded-full hover:bg-gray-100" onClick={toggleNotifications}>
                  <Bell size={20} />
                  {notificationCount > 0 && <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {notificationCount}
                    </span>}
                </button>
                
                {showNotifications && <NotificationPanel onClose={() => setShowNotifications(false)} onActionComplete={handleActionComplete} />}
              </div>
              
              <div className="relative group">
                <button className="p-2 text-gray-600 hover:text-purple-600 rounded-full hover:bg-gray-100 flex items-center">
                  <User size={20} />
                  <ChevronDown size={16} className="ml-1" />
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20 hidden group-hover:block">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{userSettings.role}</p>
                    <p className="text-xs text-gray-500">user@accenture.com</p>
                  </div>
                  <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Your Profile</a>
                  <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Settings</a>
                  <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Sign out</a>
                </div>
              </div>
              
              <div className="relative group">
                <button className="p-2 text-gray-600 hover:text-purple-600 rounded-full hover:bg-gray-100">
                  <Settings size={20} />
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20 hidden group-hover:block">
                  <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Dashboard Settings</a>
                  <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Notification Preferences</a>
                  <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Data Refresh Rate</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Key Metrics</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4 mt-4">
            <KPICard title="Network Volume" value="7,075 MML" icon={TrendingUp} positiveMetric="+9.3%" positiveLabel="vs Target" />
            
            <KPICard title="Active Campaigns" value="2" icon={Target} negativeMetric="-50%" negativeLabel="vs Plan" />
            
            <KPICard title="Customer Sentiment" value="92%" icon={Users} positiveMetric="+4%" positiveLabel="vs Last Month" />
            
            <KPICard title="Network Stations" value="2,343" icon={MapPin} positiveMetric="+12" positiveLabel="New This Month" />
            
            <KPICard title="Unit Margin" value="124 ฿/Trnx" icon={DollarSign} positiveMetric="+5.2%" positiveLabel="vs Last Month" />
            
            <KPICard title="Number of Transactions" value="2.8M" icon={TrendingUp} positiveMetric="+3.7%" positiveLabel="vs Last Month" negativeMetric="-1.2%" negativeLabel="vs Target" />
            
            <KPICard title="Avg. Transaction Value" value="845 ฿" icon={DollarSign} positiveMetric="+2.3%" positiveLabel="vs Last Month" positiveMetric="+0.8%" positiveLabel="vs Target" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recently Accessed</h3>
          <div className="flex space-x-4">
            {frequentlyAccessed.map(item => <Link key={item.title} to={item.path} className="flex-1 flex items-center bg-gray-50 hover:bg-gray-100 p-4 rounded-lg border border-gray-200 transition-colors">
                <div className="mr-3 p-2 rounded-full bg-white shadow-sm">{item.icon}</div>
                <div className="flex-1">
                  <span className="block text-sm font-medium text-gray-900">{item.title}</span>
                  <span className="block text-xs text-gray-500">Accessed {item.lastAccessed}</span>
                </div>
                <ArrowRight size={16} className="text-gray-400" />
              </Link>)}
          </div>
        </div>
        
        <div className="mb-4 text-center">
          <p className="text-gray-700">Select a module below to access specialized analytics and management capabilities.</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.map(module => <Link key={module.title} to={module.path} className={`block ${module.color} border ${module.borderColor} rounded-lg p-6 hover:shadow-md transition-shadow relative animate-fade-in`}>
              <div className="absolute top-4 right-4 flex items-center space-x-2">
                <span className={`h-3 w-3 rounded-full ${getStatusColor(module.status)}`}></span>
                <span className="text-xs text-gray-500">
                  {module.status === 'healthy' ? 'Healthy' : module.status === 'warning' ? 'Warning' : 'Critical'}
                </span>
              </div>
              
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-white rounded-md shadow-sm">
                  {module.icon}
                </div>
                <ArrowRight size={20} className="text-gray-400 mt-2" />
              </div>
              
              <h2 className="text-lg font-medium text-gray-900 mb-2">{module.title}</h2>
              <p className="text-sm text-gray-600 mb-4 h-12">{module.description}</p>
              
              <div className="grid grid-cols-3 gap-2 mt-4">
                {module.metrics.map(metric => <div key={metric.label} className="bg-white bg-opacity-60 rounded p-2">
                    <p className="text-xs text-gray-500">{metric.label}</p>
                    <div className="flex items-center justify-between">
                      <p className={`text-sm font-medium ${getPerformanceColor(metric.performance)}`}>
                        {metric.value}
                      </p>
                      {getTrendIcon(metric.trend)}
                    </div>
                  </div>)}
              </div>
            </Link>)}
        </div>
        
        <div className="mt-8 bg-purple-50 rounded-lg p-6">
          <div className="flex items-start">
            <div className="p-3 bg-purple-100 rounded-md mr-4">
              <TrendingUp size={24} className="text-purple-700" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Latest Analytics Insight</h3>
              <p className="text-sm text-gray-600 mb-4">
                Quarter-over-quarter retail performance shows a 9.3% volume increase with margins improving to 0.83 ฿/L.
                The Rural segment requires attention with a 3.7% decline while Urban Premium continues strong growth at 2.3%.
                Recent competitive analysis indicates opportunity for targeted promotions in the Northeastern region.
              </p>
              <div className="flex space-x-4">
                <button className="text-sm text-purple-700 font-medium hover:text-purple-900 flex items-center">
                  <span>View Full Report</span>
                  <ArrowRight size={16} className="ml-1" />
                </button>
                <button className="text-sm text-purple-700 font-medium hover:text-purple-900 flex items-center">
                  <span>Set Up Alerts</span>
                  <Bell size={16} className="ml-1" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Home;